import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Contract for the Settings → Approvals admin UI. Lets org admins configure
 * approval levels and rules for both the discount and order systems through
 * a single, system-parameterized API.
 *
 * Two intentional shape decisions:
 *
 * 1. Conditions are exposed in flat `{ field, operator, value }` form to the
 *    frontend, not the MongoDB-style `{ "order.totalAmount": { "$gt": 50000 } }`
 *    form the rule engine reads. Handlers translate between the two so the
 *    rule engine stays untouched and the UI stays simple. v1 supports a single
 *    condition per rule; the array shape leaves room for future AND-composition.
 *
 * 2. `system: "discount" | "order"` discriminates everywhere. The two systems
 *    have separate tables and slightly different fields (only discount levels
 *    have `productCategories`), so the contract reflects that asymmetry rather
 *    than pretending a unified shape exists.
 */

export const approvalSystemSchema = z.enum(["discount", "order"]);
export type ApprovalSystem = z.infer<typeof approvalSystemSchema>;

// ─── Conditions (flat form for the UI) ──────────────────────────────────────

export const conditionFieldSchema = z.enum(["order.totalAmount"]);

export const conditionOperatorSchema = z.enum([
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "in",
  "nin",
]);

/**
 * A single condition. Scalar operators (`eq`, `gt`, etc.) carry a `number`;
 * list operators (`in`, `nin`) carry `number[]`. Validated by the handler
 * before persistence — invalid pairings are rejected with a 400.
 */
export const ruleConditionSchema = z.object({
  field: conditionFieldSchema,
  operator: conditionOperatorSchema,
  value: z.union([z.number(), z.array(z.number())]),
});

// ─── Levels ─────────────────────────────────────────────────────────────────

export const approvalLevelSchema = z.object({
  id: z.string().uuid(),
  level: z.number().int().positive(),
  name: z.string().min(1).max(255),
  /**
   * Discount-only. Empty array means "applies to every approval". Always
   * absent on order-system levels — the schema is a union to keep the field
   * presence load-bearing.
   */
  productCategories: z.array(z.string()).optional(),
});

export type ApprovalLevelDTO = z.infer<typeof approvalLevelSchema>;

const createLevelInput = z.object({
  system: approvalSystemSchema,
  name: z.string().min(1).max(255),
  productCategories: z.array(z.string()).optional(),
});

const updateLevelInput = z.object({
  system: approvalSystemSchema,
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
  productCategories: z.array(z.string()).optional(),
});

const deleteLevelInput = z.object({
  system: approvalSystemSchema,
  id: z.string().uuid(),
});

const listLevelsInput = z.object({ system: approvalSystemSchema });

// ─── Rules ──────────────────────────────────────────────────────────────────

export const approvalRuleSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
  priority: z.number().int().positive(),
  isActive: z.boolean(),
  requiredApprovalLevels: z.number().int().positive(),
  conditions: z.array(ruleConditionSchema).min(1),
});

export type ApprovalRuleDTO = z.infer<typeof approvalRuleSchema>;

const createRuleInput = z.object({
  system: approvalSystemSchema,
  name: z.string().min(1).max(255),
  isActive: z.boolean(),
  requiredApprovalLevels: z.number().int().positive(),
  conditions: z.array(ruleConditionSchema).min(1).max(1),
});

const updateRuleInput = z.object({
  system: approvalSystemSchema,
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
  isActive: z.boolean(),
  requiredApprovalLevels: z.number().int().positive(),
  conditions: z.array(ruleConditionSchema).min(1).max(1),
});

const deleteRuleInput = z.object({
  system: approvalSystemSchema,
  id: z.string().uuid(),
});

const listRulesInput = z.object({ system: approvalSystemSchema });

const reorderRulesInput = z.object({
  system: approvalSystemSchema,
  /** Rule IDs in their new order; index 0 becomes priority 1. */
  orderedIds: z.array(z.string().uuid()).min(1),
});

const toggleRuleActiveInput = z.object({
  system: approvalSystemSchema,
  id: z.string().uuid(),
  isActive: z.boolean(),
});

// ─── Approver assignments ──────────────────────────────────────────────────
//
// An assignment binds a user to a level for a specific customer or for every
// customer in the org (`customerId: null`). The DTO denormalizes display
// fields (level name + number, customer name, user name) so the list view
// can render without N+1 reads from the frontend.

export const approvalAssignmentSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  userName: z.string(),
  /** The user's primary email — shown alongside the override so admins can see
   * the default routing target. Null when the user has no primary email. */
  userPrimaryEmail: z.string().nullable(),
  /** When set, approval emails for this assignment route here instead of the
   * user's primary email. Null means "use the primary email." */
  emailOverride: z.string().nullable(),
  levelId: z.string().uuid(),
  levelNumber: z.number().int().positive(),
  levelName: z.string(),
  /** Null = the assignment applies to every customer in the organization. */
  customerId: z.string().uuid().nullable(),
  /** Null when `customerId` is null. */
  customerName: z.string().nullable(),
});

export type ApprovalAssignmentDTO = z.infer<typeof approvalAssignmentSchema>;

const listAssignmentsInput = z.object({ system: approvalSystemSchema });

const createAssignmentInput = z.object({
  system: approvalSystemSchema,
  userId: z.string().uuid(),
  levelId: z.string().uuid(),
  /** Null grants authority for every customer in the organization. */
  customerId: z.string().uuid().nullable(),
  /** Optional. When set, approval emails route here instead of the user's
   * primary email. Empty string is treated as null. */
  emailOverride: z.string().email().nullable().optional(),
});

const updateAssignmentInput = z.object({
  system: approvalSystemSchema,
  id: z.string().uuid(),
  /** Null clears the override so notifications fall back to the primary email. */
  emailOverride: z.string().email().nullable(),
});

const deleteAssignmentInput = z.object({
  system: approvalSystemSchema,
  id: z.string().uuid(),
});

// ─── Procedures ─────────────────────────────────────────────────────────────

export const approvalConfigContracts = {
  listLevels: oc
    .input(listLevelsInput)
    .output(z.object({ levels: z.array(approvalLevelSchema) }))
    .route({ method: "GET", summary: "List approval levels for a system" }),

  createLevel: oc.input(createLevelInput).output(approvalLevelSchema).route({
    method: "POST",
    summary: "Create a new approval level (auto-numbered)",
  }),

  updateLevel: oc.input(updateLevelInput).output(approvalLevelSchema).route({
    method: "POST",
    summary: "Update a level's name (and product categories on discount)",
  }),

  deleteLevel: oc
    .input(deleteLevelInput)
    .output(z.object({ success: z.literal(true) }))
    .route({
      method: "POST",
      summary:
        "Delete a level; refuses if any rule or pending approval refs it",
    }),

  listRules: oc
    .input(listRulesInput)
    .output(z.object({ rules: z.array(approvalRuleSchema) }))
    .route({ method: "GET", summary: "List approval rules for a system" }),

  createRule: oc.input(createRuleInput).output(approvalRuleSchema).route({
    method: "POST",
    summary: "Create a rule appended at the lowest priority",
  }),

  updateRule: oc.input(updateRuleInput).output(approvalRuleSchema).route({
    method: "POST",
    summary: "Update a rule's name, condition, requiredLevels, isActive",
  }),

  deleteRule: oc
    .input(deleteRuleInput)
    .output(z.object({ success: z.literal(true) }))
    .route({
      method: "POST",
      summary: "Delete a rule; refuses if any active approval references it",
    }),

  reorderRules: oc
    .input(reorderRulesInput)
    .output(z.object({ rules: z.array(approvalRuleSchema) }))
    .route({
      method: "POST",
      summary:
        "Rewrite priorities to match the supplied order, transactionally",
    }),

  toggleRuleActive: oc
    .input(toggleRuleActiveInput)
    .output(approvalRuleSchema)
    .route({
      method: "POST",
      summary: "Flip a rule's isActive flag",
    }),

  listAssignments: oc
    .input(listAssignmentsInput)
    .output(z.object({ assignments: z.array(approvalAssignmentSchema) }))
    .route({
      method: "GET",
      summary: "List approver assignments for a system",
    }),

  createAssignment: oc
    .input(createAssignmentInput)
    .output(approvalAssignmentSchema)
    .route({
      method: "POST",
      summary:
        "Bind a user to a level for one customer or every customer (null)",
    }),

  updateAssignment: oc
    .input(updateAssignmentInput)
    .output(approvalAssignmentSchema)
    .route({
      method: "POST",
      summary: "Set or clear the email override on an assignment",
    }),

  deleteAssignment: oc
    .input(deleteAssignmentInput)
    .output(z.object({ success: z.literal(true) }))
    .route({
      method: "POST",
      summary: "Remove an approver assignment",
    }),
} as const;
